import React, { useState, useEffect, lazy, Suspense } from "react"
import { useSelector } from "react-redux"
import NavButtons from "../components/kpi-dashboard/NavButtons"
import useFetchRoiMetrics from "../hooks/useFetchRoiMetrics"
import useFetchCenters from "../hooks/useFetchCenters"
import moment from "moment-timezone"
import { getDateForString, getDayOfWeek } from "../utils/generalUtils"
import BlankCard from "../components/kpi-dashboard/BlankCard"
import {
	createGraphProps,
	calculateProcessMetrics
} from "../components/kpi-dashboard/graphCalculations"
import KPIDashboardSkeleton, {
	GraphSkeleton
} from "../components/kpi-dashboard/KPIDashboardSkeleton"
import useFetchTemplates from "../hooks/useFetchTemplates"
import useLogging from "../hooks/useLogging"
import useFetchSettings from "../hooks/useFetchSettings"

// ProcessOverview pulls in the chart-heavy graph components (ApexCharts +
// many subgraphs). Deferring it lets the cards/header paint immediately and
// the graphs fade in once their bundle arrives.
const ProcessOverview = lazy(() =>
	import("../components/kpi-dashboard/ProcessOverview")
)

// Function to filter data by selected days (array of days)
export const filterByDays = (data, selectedDays) => {
	return data.filter(item => {
		// Get the day of the week for the schedule's date (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
		const dayOfWeek = getDayOfWeek(item.date)

		return selectedDays.includes(dayOfWeek)
	})
}

function KPIDashboard({ sidebarOpen }) {
	// Fetch settings and permissions from Redux store
	const { settingsData } = useFetchSettings()
	const { templatesData } = useFetchTemplates()
	const goals = settingsData?.goals || []
	const logging = useLogging()

	logging.info(`Page Visited: KPIDashboard`)
	// Fetch ROI metrics data
	const { roiMetrics } = useFetchRoiMetrics()

	// const startDate = settingsData?.liveDate
	// 	? new Date(moment.tz(settingsData.liveDate, "America/Los_Angeles"))
	// 	: roiMetrics?.length > 0
	// 		? new Date(roiMetrics[0].date)
	// 		: new Date()
	const startDate = new Date("2025-02-14")
	const yesterday = moment().subtract(1, "days").toDate()
	const [dateRange, setDateRange] = useState({
		startDate: new Date("2025-03-17"),
		endDate: yesterday
	}) // Update state for new date picker

	useEffect(() => {
		if (dateRange.startDate === null || dateRange.endDate === null) {
			setDateRange({ startDate: startDate, endDate: yesterday })
		}
		// React setState setters are stable — omitting deps that are setter-only or initial-load reads.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startDate])

	const centers = useFetchCenters()
	const center = useSelector(state => state.settings.center)
	const [selectedLocation, setSelectedLocation] = useState(center?.name)
	const [selectedDays, setSelectedDays] = useState([]) // To store selected days

	const [completedVolumesViewState, setCompletedVolumesViewState] = useState("day")
	const [patientWaitViewState, setPatientWaitViewState] = useState("day")
	const [chairTimeUtilizationViewState, setChairTimeUtilizationViewState] = useState("day")

	useEffect(() => {
		if (selectedLocation === null || selectedLocation === undefined) {
			setSelectedLocation(center?.name)
		}
		// React setState setters are stable — omitting deps that are setter-only or initial-load reads.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [center])

	// Add these state variables at the top level
	const [treatmentModalRange, setTreatmentModalRange] = useState("1month")
	const [nurseWorkloadType, setNurseWorkloadType] = useState("acuity")

	if (!roiMetrics || roiMetrics.length === 0 || !centers || !templatesData) {
		return <KPIDashboardSkeleton sidebarOpen={sidebarOpen} />
	}

	const today = new Date()

	const historicalData = filterByDays(
		roiMetrics.filter(item => {
			const itemDate = getDateForString(item.date)
			return (
				itemDate >= getDateForString(dateRange.startDate) &&
				itemDate <= getDateForString(dateRange.endDate)
			)
		}),
		selectedDays
	)

	// Calculate process metrics for the overview section
	const { positiveMetrics, attentionMetrics } = calculateProcessMetrics(
		historicalData,
		roiMetrics,
		selectedDays,
		settingsData,
		dateRange,
		logging
	)

	return (
		<div>
			<div className={`${sidebarOpen ? "pl-64" : "pl-8"}`}>
				<div className="my-5 flex h-16 shrink-0 items-center justify-between bg-white px-4 sm:px-6 lg:px-8">
					<h1 className="font-bold text-4xl font-manrope text-custom-blue">
						Operational Reports
					</h1>
				</div>

				<div className="bg-white px-4 sm:px-6 lg:px-8">
					<div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6">
						<NavButtons
							selectedLocation={selectedLocation}
							setSelectedLocation={setSelectedLocation}
							selectedDays={selectedDays}
							setSelectedDays={setSelectedDays}
							centers={centers}
							dateRange={dateRange}
							setDateRange={setDateRange}
							MIN_DATE={startDate}
							MAX_DATE={today}
						/>
					</div>
				</div>

				<main>
					<div className="px-4 sm:px-6 lg:px-8 pt-5">
						{/* Process Overview */}
						<Suspense fallback={<GraphSkeleton height="h-80" />}>
							<ProcessOverview
								positiveMetrics={positiveMetrics}
								attentionMetrics={attentionMetrics}
							/>
						</Suspense>
					</div>

					<div className="px-4 sm:px-6 lg:px-8 py-10">
						<div>
							{/* First Row */}
							<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-6">
								{/* Completed Volumes Graph */}
								<div className="text-center">
									<BlankCard
										{...createGraphProps(
											"completedVolumes",
											historicalData.map(day => ({
												date: day.date,
												completedVolumes:
													day.patient_volumes?.completed || 0,
												linkedAppointments:
													day.patient_volumes?.linked || 0,
												averageAppointmentsPerChair:
													day.average_appointments_per_chair || 0
											})),
											{
												height: 450,
												chartHeight: 230,
												goal:
													goals.find(g => g.name === "Capacity Goal (%)")
														?.value || 70
											},
											completedVolumesViewState,
											setCompletedVolumesViewState
										)}
									/>
								</div>
								{/* Patient Wait Time Graph */}
								<div className="text-center">
									<BlankCard
										{...createGraphProps(
											"patientWait",
											historicalData.map(day => ({
												date: day.date,
												averageWaitTime:
													day.patient_wait_time
														?.averageWaitTimePerPatient || 0,
												waitTimeDistribution: day.waitTimeDistribution || {}
											})),
											{
												height: 450,
												chartHeight: 230,
												goal:
													goals.find(
														g =>
															g.name ===
															"Patient Wait Time Goal (min)"
													)?.value || 15
											},
											patientWaitViewState,
											setPatientWaitViewState
										)}
									/>
								</div>
								{/* Chair Time Utilization Graph */}
								<div className="text-center">
									<BlankCard
										{...createGraphProps(
											"chairTimeUtilization",
											historicalData.map(day => ({
												date: day.date,
												chairTimeUtilization:
													day.chair_utilization?.total || 0,
												chair_utilization: day.chair_utilization || {}
											})),
											{
												height: 450,
												chartHeight: 230,
												goal:
													goals.find(
														g =>
															g.name ===
															"Chair Time Utilization Goal (%)"
													)?.value || 90
											},
											chairTimeUtilizationViewState,
											setChairTimeUtilizationViewState
										)}
									/>
								</div>
							</div>

							{/* Second Row */}
							<div className="grid gap-6 grid-cols-1 md:grid-cols-3 lg:grid-cols-3 mb-6">
								{/* Nurse Workload Graph */}
								<div className="text-center md:col-span-2 lg:col-span-2">
									<BlankCard
										{...createGraphProps(
											"nurseWorkload",
											historicalData.map(day => ({
												date: day.date,
												hourly: day.nurse_workload?.hourly,
												hourlyPatients: day.nurse_workload?.hourlyPatients,
												averageAcuityPerNurse:
													day.nurse_workload?.averageAcuityPerNurse,
												averagePatientsPerNurse:
													day.nurse_workload?.averagePatientsPerNurse,
												averageOvertimePerDay: day?.averageOvertimePerDay
											})),
											{
												height: 450,
												chartHeight: 250,
												type: nurseWorkloadType,
												openTime: settingsData?.openTime[0] || "8:00",
												closeTime: settingsData?.closeTime[0] || "17:00",
												onTypeChange: setNurseWorkloadType
											}
										)}
									/>
								</div>
								{/* Treatment Variance Card */}
								<div className="text-center">
									<BlankCard
										{...createGraphProps(
											"treatmentVariance",
											historicalData, // Pass the raw historical data directly
											{
												height: 450,
												chartHeight: 350,
												threshold: 20,
												sampleSizeThreshold: 20,
												limit: 3,
												selectedRange: treatmentModalRange,
												setSelectedRange: setTreatmentModalRange
												// No need to pass rawData anymore
											}
										)}
									/>
								</div>
							</div>

							{/* Third Row */}
							<div className="grid gap-6 grid-cols-1 md:grid-cols-3 lg:grid-cols-3 mb-6">
								{/* Template Compliance Graph */}
								<div className="text-center md:col-span-3 lg:col-span-3">
									<BlankCard
										{...createGraphProps(
											"templateCompliance",
											historicalData.map(day => ({
												date: day.date,
												compliance: day.template_compliance?.percentage || 0
											})),
											{
												height: 450,
												chartHeight: 325,
												thresholds: {
													low:
														goals.find(
															g =>
																g.name ===
																"Template Compliance Low Threshold (%)"
														)?.value || 70,
													high:
														goals.find(
															g =>
																g.name ===
																"Template Compliance Goal (%)"
														)?.value || 80
												},
												templateDates: templatesData
													.filter(
														template =>
															template.day_of_week === "Monday"
													)
													.map(template => template.startDate),
												selectedDays: selectedDays
											}
										)}
									/>
								</div>
							</div>

							{/* Fourth Row */}
							<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
								{/* Patient Punctuality Graph */}
								<div className="text-center">
									<BlankCard
										{...createGraphProps(
											"patientPunctuality",
											historicalData.map(day => ({
												date: day.date,
												punctuality: day.patient_punctuality
											})),
											{
												height: 450,
												chartHeight: 200,
												earlyThreshold: 30,
												lateThreshold: 30,
												highThreshold: 80,
												lowThreshold: 60
											}
										)}
									/>
								</div>
								{/* Add Ons and Cancellations Graph */}
								<div className="text-center">
									<BlankCard
										{...createGraphProps(
											"addOnCancellations",
											historicalData.map(day => ({
												date: day.date,
												hourly: day.add_ons_and_cancellations?.hourly || {},
												total:
													day.add_ons_and_cancellations?.totalPatients ||
													0,
												addOns:
													day.add_ons_and_cancellations?.daily.addOns ||
													0,
												cancellations:
													day.add_ons_and_cancellations?.daily
														.cancellations || 0,
												noShows:
													day.add_ons_and_cancellations?.daily.noShows ||
													0,
												patient_volumes: day.patient_volumes || {}
											})),
											{
												height: 450,
												chartHeight: 200
											}
										)}
									/>
								</div>
								{/* Acuity Load Graph */}
								<div className="text-center">
									<BlankCard
										{...createGraphProps(
											"patientAcuity",
											historicalData.map(day => ({
												date: day.date,
												acuity_load: day.acuity_load?.daily
											})),
											{
												height: 450,
												chartHeight: 300,
												selectedDays: selectedDays
											}
										)}
									/>
								</div>
							</div>
						</div>
					</div>
				</main>
			</div>
		</div>
	)
}

export default KPIDashboard
